import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';

const VIDEO_URL = 'https://customer-assets.emergentagent.com/job_5ecc19a3-715e-40b2-bee7-8ae4aeb4c8cb/artifacts/n0awahpz_xmx.mp4';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_5ecc19a3-715e-40b2-bee7-8ae4aeb4c8cb/artifacts/ladwjbmi_4926BBCA-F27B-484F-9467-294F35FF2347.PNG';

/* ─── Reveal wrapper ─── */
function RevealText({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <div ref={ref} className="overflow-hidden">
      <motion.div
        className={className}
        initial={{ y: '110%', opacity: 0 }}
        animate={isInView ? { y: '0%', opacity: 1 } : { y: '110%', opacity: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
}

/* ─── PREMIUM CTA BUTTON ─── */
function PremiumCTA() {
  return (
    <Link to="/contact" data-testid="hero-cta">
      <motion.div
        className="cta-orbit-container group relative inline-flex items-center justify-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Orbiting SVG — oversized viewBox for soft fade */}
        <svg
          className="absolute pointer-events-none"
          style={{ width: 'calc(100% + 40px)', height: 'calc(100% + 40px)', left: '-20px', top: '-20px', overflow: 'visible' }}
          viewBox="0 0 240 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <filter id="orb-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
            </filter>
          </defs>
          {/* Base border — centered in the larger viewBox */}
          <rect
            x="20" y="20" width="200" height="60" rx="30"
            stroke="rgba(255,255,255,0.10)" strokeWidth="1" fill="none"
            className="cta-orbit-base"
          />
          {/* Animated arc tracing the perimeter */}
          <rect
            x="20" y="20" width="200" height="60" rx="30"
            stroke="#EE3602" strokeWidth="1" fill="none"
            strokeDasharray="80 436"
            className="cta-orbit-path"
          />
          {/* Orbiting dot — glow layer */}
          <circle r="5" fill="#EE3602" opacity="0.35" filter="url(#orb-glow)">
            <animateMotion
              dur="6s"
              repeatCount="indefinite"
              path="M120,20 A30,30 0 0,1 220,50 A30,30 0 0,1 120,80 L120,80 A30,30 0 0,1 20,50 A30,30 0 0,1 120,20"
            />
          </circle>
          {/* Orbiting dot — core */}
          <circle r="2.5" fill="#EE3602" className="cta-orbit-dot">
            <animateMotion
              dur="6s"
              repeatCount="indefinite"
              path="M120,20 A30,30 0 0,1 220,50 A30,30 0 0,1 120,80 L120,80 A30,30 0 0,1 20,50 A30,30 0 0,1 120,20"
            />
          </circle>
        </svg>

        <span className="relative z-10 px-10 py-4 font-mono text-xs tracking-[0.25em] uppercase text-xmx-grey group-hover:text-xmx-white transition-colors duration-500">
          Get in touch
        </span>
      </motion.div>
    </Link>
  );
}

/* ─── HERO SECTION ─── */
function HeroSection() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.35, 0.6, 1]);
  const textY = useTransform(scrollYProgress, [0, 0.6], ['0%', '20%']);
  const textOpacity = useTransform(scrollYProgress, [0, 0.35], [1, 0]);

  return (
    <section ref={containerRef} className="hero-video-container" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ scale: videoScale }}>
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
          src={VIDEO_URL}
        />
      </motion.div>

      <motion.div
        className="absolute inset-0 bg-xmx-black z-[2]"
        style={{ opacity: overlayOpacity }}
      />

      <motion.div
        className="absolute inset-0 z-[3] flex flex-col items-center justify-end pb-28 sm:pb-36"
        style={{ y: textY, opacity: textOpacity }}
      >
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1 className="font-cabinet text-5xl sm:text-7xl lg:text-[8vw] leading-[0.85] tracking-tighter uppercase font-black text-xmx-white">
            XMX
          </h1>
          <div className="mt-2 sm:mt-3">
            <span className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-xmx-grey">
              Ventures
            </span>
          </div>
        </motion.div>

        <div className="mt-8 sm:mt-12">
          <PremiumCTA />
        </div>
      </motion.div>

      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-xmx-black to-transparent z-[4] pointer-events-none" />
    </section>
  );
}

/* ─── THESIS SECTION ─── */
function ThesisSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const words = ['Control', 'Scale', 'Precision', 'Execution'];

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[120vh] flex items-center justify-center bg-xmx-black"
      data-testid="thesis-section"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-12 w-full">
        <div className="space-y-4 sm:space-y-6">
          {words.map((word, i) => {
            const start = i * 0.15;
            const end = start + 0.2;
            return (
              <ThesisWord
                key={word}
                word={word}
                index={i}
                scrollYProgress={scrollYProgress}
                start={start}
                end={end}
              />
            );
          })}
        </div>

        <div className="mt-16 sm:mt-24 max-w-lg">
          <RevealText delay={0.2}>
            <p className="font-mono text-sm sm:text-base tracking-wide leading-relaxed text-xmx-grey">
              We build and scale consumer brands for the U.S. market.
              Product development, supply chain coordination, and market
              deployment — unified into a single, controlled system.
            </p>
          </RevealText>
        </div>
      </div>
    </section>
  );
}

function ThesisWord({ word, index, scrollYProgress, start, end }) {
  const opacity = useTransform(scrollYProgress, [start, end], [0.12, 1]);
  const x = useTransform(scrollYProgress, [start, end], [-30, 0]);

  return (
    <motion.div style={{ opacity, x }} className="flex items-baseline gap-4 sm:gap-6">
      <span className="font-mono text-[10px] tracking-[0.2em] text-xmx-accent uppercase">
        {String(index + 1).padStart(2, '0')}
      </span>
      <h2 className="font-cabinet text-4xl sm:text-6xl lg:text-7xl tracking-tight font-bold uppercase text-xmx-white">
        {word}
      </h2>
    </motion.div>
  );
}

/* ─── DIVIDER ─── */
function Divider() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <div ref={ref} className="relative py-24 sm:py-32 bg-xmx-black overflow-hidden">
      <motion.div
        className="h-[1px] bg-gradient-to-r from-transparent via-xmx-accent/40 to-transparent mx-6 sm:mx-12"
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
      />
    </div>
  );
}

/* ─── CAPABILITIES SECTION ─── */
function CapabilitiesSection() {
  const capabilities = [
    { title: 'Brand Creation', detail: 'Identity systems built for scale' },
    { title: 'Product Development', detail: 'From concept to market-ready execution' },
    { title: 'Supply Chain', detail: 'End-to-end coordination and logistics' },
    { title: 'Digital Commerce', detail: 'Full-channel deployment and optimization' },
  ];

  return (
    <section
      className="relative bg-xmx-black py-24 sm:py-40 px-6 sm:px-12"
      data-testid="capabilities-section"
    >
      <div className="max-w-6xl mx-auto">
        <RevealText>
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-12 sm:mb-20">
            What we do
          </span>
        </RevealText>

        <div className="space-y-0">
          {capabilities.map((cap, i) => (
            <CapabilityRow key={cap.title} capability={cap} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CapabilityRow({ capability, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      className="capability-row group py-8 sm:py-10 flex items-baseline justify-between cursor-default"
      data-testid={`capability-${index}`}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="flex items-baseline gap-4 sm:gap-8">
        <span className="font-mono text-[10px] tracking-[0.2em] text-xmx-muted group-hover:text-xmx-accent transition-colors duration-500">
          {String(index + 1).padStart(2, '0')}
        </span>
        <h3 className="font-cabinet text-xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-xmx-white uppercase">
          {capability.title}
        </h3>
      </div>
      <span className="font-mono text-xs tracking-wide text-xmx-muted group-hover:text-xmx-grey transition-colors duration-500 hidden sm:block">
        {capability.detail}
      </span>
    </motion.div>
  );
}

/* ─── STATEMENT SECTION ─── */
function StatementSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'center center'],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [0, 1]);
  const y = useTransform(scrollYProgress, [0, 0.5], [60, 0]);

  return (
    <section
      ref={ref}
      className="relative min-h-[80vh] flex items-center bg-xmx-black px-6 sm:px-12"
      data-testid="statement-section"
    >
      <motion.div className="max-w-5xl mx-auto" style={{ opacity, y }}>
        <h2 className="font-cabinet text-3xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-bold text-xmx-white">
          We don't follow markets.
          <br />
          <span className="text-xmx-grey">We build them.</span>
        </h2>
      </motion.div>
    </section>
  );
}

/* ─── TICKER ─── */
function TickerSection() {
  return (
    <div className="relative py-16 sm:py-24 bg-xmx-black overflow-hidden border-t border-b border-white/[0.04]">
      <div className="flex whitespace-nowrap">
        <div className="ticker-track flex gap-16 sm:gap-24">
          {Array.from({ length: 8 }).map((_, i) => (
            <span
              key={i}
              className="font-cabinet text-6xl sm:text-8xl lg:text-[10vw] font-black uppercase tracking-tighter text-white/[0.03] select-none"
            >
              XMX Ventures
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── ENTERPRISE FOOTER ─── */
function EnterpriseFooter() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });

  const footerLinks = [
    { label: 'About', path: '/about' },
    { label: 'Capabilities', path: '/capabilities' },
    { label: 'Contact', path: '/contact' },
  ];

  return (
    <footer
      ref={ref}
      className="relative bg-xmx-black border-t border-white/[0.06]"
      data-testid="footer"
    >
      {/* Main footer */}
      <div className="px-6 sm:px-12 pt-20 sm:pt-28 pb-12">
        <div className="max-w-6xl mx-auto">
          {/* Top row: Logo + CTA */}
          <motion.div
            className="flex flex-col lg:flex-row justify-between items-start gap-12 lg:gap-0 mb-20 sm:mb-28"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div>
              <img src={LOGO_URL} alt="XMX Ventures" className="h-14 sm:h-18 w-auto mb-6" />
              <p className="font-mono text-xs tracking-wide text-xmx-muted max-w-xs leading-relaxed">
                Modern commerce operator building and scaling consumer brands for the U.S. market.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="footer-contact-link"
              className="font-mono text-xs tracking-[0.2em] uppercase text-xmx-grey hover:text-xmx-accent transition-colors duration-400 border-b border-xmx-accent/30 pb-1"
            >
              Start a conversation
            </Link>
          </motion.div>

          {/* Middle row: Nav + Info columns */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-3 gap-12 sm:gap-8 mb-20 sm:mb-28"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Navigation */}
            <div>
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-6">
                Navigation
              </span>
              <div className="flex flex-col gap-4">
                {footerLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="nav-link font-mono text-sm text-xmx-grey hover:text-xmx-white transition-colors duration-300 w-fit"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Focus areas */}
            <div>
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-6">
                Focus
              </span>
              <div className="flex flex-col gap-4">
                {['Brand Creation', 'Product Development', 'Supply Chain', 'Digital Commerce'].map((item) => (
                  <span key={item} className="font-mono text-sm text-xmx-muted">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Location & Contact */}
            <div>
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-6">
                Headquarters
              </span>
              <div className="flex flex-col gap-4">
                <span className="font-mono text-sm text-xmx-grey leading-relaxed">
                  XMX Ventures LLC
                  <br />
                  233 N Michigan Ave, Suite 2800
                  <br />
                  Chicago, IL 60601
                </span>
                <a href="mailto:hello@xmxventures.com" className="font-mono text-sm text-xmx-muted hover:text-xmx-white transition-colors duration-300">
                  hello@xmxventures.com
                </a>
              </div>
            </div>
          </motion.div>

          {/* Bottom row: Legal */}
          <motion.div
            className="flex items-start sm:items-center justify-start pt-8 border-t border-white/[0.04]"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-xmx-muted">
              {new Date().getFullYear()} XMX Ventures. All rights reserved.
            </span>
          </motion.div>
        </div>
      </div>
    </footer>
  );
}

/* ─── HOME PAGE ─── */
export default function Home() {
  return (
    <div data-testid="home-page" className="bg-xmx-black">
      <HeroSection />
      <ThesisSection />
      <Divider />
      <CapabilitiesSection />
      <StatementSection />
      <TickerSection />
      <EnterpriseFooter />
    </div>
  );
}
